import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { SavedItemsProvider } from './contexts/SavedItemsContext';
import { VisitsProvider } from './contexts/VisitsContext';
import PrivateRoute from './components/PrivateRoute';
import Layout from './components/Layout';
import ChatPage from './pages/ChatPage';
import VisitsPage from './pages/VisitsPage';
import SavedPage from './pages/SavedPage';
import ComparePage from './pages/ComparePage';
import CompoundPage from './pages/CompoundPage';
import AccountPage from './pages/AccountPage';
import LoginPage from './pages/LoginPage';
import SignUpPage from './pages/SignUpPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import GoogleCallback from './pages/GoogleCallback';
import FacebookCallback from './pages/FacebookCallback';
import { BACKEND_URL } from './environment';
import './App.css';

function App() {
  const [backendStatus, setBackendStatus] = useState('checking');
  const backendUrl = BACKEND_URL;

  const checkBackendStatus = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      const response = await fetch(`${backendUrl}/health`, {
        signal: controller.signal,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      clearTimeout(timeoutId);
      if (response.ok) setBackendStatus('connected');
      else setBackendStatus('error');
    } catch (error) {
      console.warn('Backend not accessible:', error);
      setBackendStatus('disconnected');
    }
  }, [backendUrl]);

  useEffect(() => {
    checkBackendStatus();
  }, [checkBackendStatus]);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <SavedItemsProvider>
          <VisitsProvider>
          <Routes>
            {/* Public auth routes — no layout chrome */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/auth/google/callback" element={<GoogleCallback />} />
            <Route path="/auth/facebook/callback" element={<FacebookCallback />} />

            {/* App routes — inside Layout. Chat is open to guests; other
                routes are individually gated behind PrivateRoute. */}
            <Route
              path="/"
              element={
                <Layout
                  backendUrl={backendUrl}
                  backendStatus={backendStatus}
                  onRetryConnection={checkBackendStatus}
                />
              }
            >
              <Route index element={<ChatPage />} />
              <Route path="chat" element={<ChatPage />} />
              {/* A company's advisor link. Same chat surface; the server
                  resolves the slug to a company and returns its branding. */}
              <Route path="a/:slug" element={<ChatPage />} />
              <Route path="visits" element={<PrivateRoute><VisitsPage /></PrivateRoute>} />
              <Route path="saved" element={<PrivateRoute><SavedPage /></PrivateRoute>} />
              <Route path="compare" element={<PrivateRoute><ComparePage /></PrivateRoute>} />
              <Route path="compound/:id" element={<PrivateRoute><CompoundPage /></PrivateRoute>} />
              <Route path="account" element={<PrivateRoute><AccountPage /></PrivateRoute>} />
            </Route>
          </Routes>
          </VisitsProvider>
          </SavedItemsProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
